import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { mediaUrl, releaseUrl } from '../data/media';
import { supplierDecision, supplierRating, mainPhoto, priceLine, primaryContact } from '../rules';
import { Stars } from './controls';
import { tr } from '../i18n';

/* ═══════════════════════════════════════════════════════════════════════════
   Pills
   ═════════════════════════════════════════════════════════════════════════ */

const TONE = {
  Strong: 'strong',
  APPROVED: 'strong',
  Verified: 'strong',
  Received: 'strong',
  Maybe: 'maybe',
  HOLD: 'maybe',
  Pending: 'maybe',
  Ordered: 'maybe',
  No: 'no',
  REJECTED: 'no',
  Problem: 'no',
};

export function Pill({ children, tone, className = '' }) {
  // The tone is keyed on the RAW English value: decisions and next steps are
  // data, so a pill must not change colour when the language changes. Only
  // the rendered label is translated.
  const t = tone || TONE[children] || 'info';
  return (
    <span className={`ft-pill ft-pill-${t} ${className}`}>
      {typeof children === 'string' ? tr(children) : children}
    </span>
  );
}

export function DecisionPill({ supplier }) {
  const d = supplierDecision(supplier);
  return <Pill>{d}</Pill>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Collapsible block (spec § 2) — "More details (optional)" and the review
   sections A–J.
   ═════════════════════════════════════════════════════════════════════════ */

export function Block({ title, children, defaultOpen = false, badge, onOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  /** `onOpen` fires the first time a block is expanded — used to preload a
   *  code-split chunk (the translation table) just before it is needed. */
  const toggle = () => {
    setOpen((o) => {
      if (!o && onOpen) onOpen();
      return !o;
    });
  };

  return (
    <section className="ft-block">
      <button type="button" className="ft-block-head" aria-expanded={open} onClick={toggle}>
        <span className="ft-block-title">{title}</span>
        {badge != null && <span className="ft-block-badge">{badge}</span>}
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} />
      </button>
      {open && <div className="ft-block-body">{children}</div>}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Snackbar

   The same component the Loans app uses (its `Snackbar`, spec section 04):
   a light card, a coloured left edge, a round tinted icon badge, the message,
   a dismiss button, and a progress bar that drains over the lifetime so the
   reader can see how long it has left. Replaces Fair Tracker's original dark
   1.8s toast, so a save confirmation looks the same in both apps.

   Colours come from Fair Tracker's OWN tokens rather than Loans' literals,
   because this app has a dark theme and hard-coded #FFFFFF would have been a
   white slab in it. Same shape, right palette.
   ═════════════════════════════════════════════════════════════════════════ */

/** Keep in sync with the ft-toast-progress duration in fairTrackerScreens.css. */
const TOAST_MS = 3000;

const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

/**
 * The app root's theme, copied onto the toast layer.
 *
 * The layer is a SECOND .ft-root (see the wrapper below), and dark mode is
 * selected by `.ft-root:not([data-ft-theme='light'])` under
 * prefers-color-scheme. A layer with no attribute therefore resolved its theme
 * independently of the app — on a light-themed app in a dark-mode browser the
 * result was a dark navy slab floating over light screens.
 *
 * `undefined` is the correct answer when the app itself carries no attribute:
 * that is the 'auto' setting, where both roots follow the OS together.
 */
function appThemeAttr() {
  if (typeof document === 'undefined') return undefined;
  const root = document.querySelector('.ft-root[data-ft-theme]');
  return (root && root.getAttribute('data-ft-theme')) || undefined;
}

const ToastCheck = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ToastAlert = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

export function ToastHost({ children }) {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);

  const dismiss = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setMsg(null);
  }, []);

  /**
   * @param severity 'success' (default) or 'error' — picks the tick/alert icon
   *   and the green/red edge. Defaulted so the ~35 plain `toast('Saved')` calls
   *   keep working untouched.
   */
  const toast = useCallback((text, severity = 'success') => {
    if (!text) return;
    setMsg({ text, severity, key: Date.now() });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(null), TOAST_MS);
  }, []);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const isError = !!msg && msg.severity === 'error';

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {msg && (
        /* THE .ft-root WRAPPER IS LOAD-BEARING, do not remove it.
           ToastHost deliberately sits ABOVE FairTrackerShell (so a toast
           outlives the screen that raised it), which also puts it OUTSIDE the
           .ft-root element that shell renders. Every toast rule is written
           `.ft-root .ft-toast`, and the design tokens it needs
           (--ft-tabbar-space, --ft-gold) are declared on .ft-root — so the
           bare element matched nothing: it computed to `position: static`,
           `z-index: auto`, and laid out ~1270px down a 889px viewport. The
           toast was in the DOM on every save; it was simply never on screen.
           This wrapper restores both the selector match and the tokens. */
        <div className="ft-root ft-toast-layer" data-ft-theme={appThemeAttr()}>
          {/* key on the card, not the text: a second toast arriving while the
              first is up must restart both the entry animation and the
              progress bar, which only happens if React remounts the node. */}
          <div
            className={`ft-toast${isError ? ' ft-toast-error' : ''}`}
            role={isError ? 'alert' : 'status'}
            aria-live={isError ? 'assertive' : 'polite'}
            key={msg.key}
          >
            <div className="ft-toast-row">
              <span className="ft-toast-ic">{isError ? <ToastAlert /> : <ToastCheck />}</span>
              <span className="ft-toast-msg">{msg.text}</span>
              <button type="button" className="ft-toast-x" onClick={dismiss} aria-label={tr('Dismiss')}>
                <Icon name="X" size={16} />
              </button>
            </div>
            <span className="ft-toast-bar" />
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Modal / bottom sheet (spec § 2)
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * A bottom sheet on phones. Actions sit at the TOP on the note modal (spec
 * § 5.6) because the on-screen keyboard covers the bottom half while typing —
 * `actionsTop` is that case.
 */
export function Modal({ title, onClose, children, actions, actionsTop = false, wide = false }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ft-modal-wrap" role="dialog" aria-modal="true" aria-label={title || 'Dialog'}>
      <button type="button" className="ft-modal-scrim" aria-label={tr("Close")} onClick={onClose} />
      <div className={`ft-modal${wide ? ' ft-modal-wide' : ''}`}>
        <header className="ft-modal-head">
          <h2 className="ft-h2">{title}</h2>
          <button type="button" className="ft-icon-btn" onClick={onClose} aria-label={tr("Close")}>
            <Icon name="X" size={18} />
          </button>
        </header>
        {actionsTop && actions && <div className="ft-modal-actions ft-modal-actions-top">{actions}</div>}
        <div className="ft-modal-body">{children}</div>
        {!actionsTop && actions && <div className="ft-modal-actions">{actions}</div>}
      </div>
    </div>
  );
}

/** Confirm dialog. Destructive by default — everything that calls it deletes. */
export function Confirm({ title, message, confirmLabel = 'Delete', onConfirm, onCancel }) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      actions={
        <>
          <button type="button" className="ft-btn" onClick={onCancel}>
            {tr("Cancel")}
          </button>
          <button type="button" className="ft-btn ft-btn-danger" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="ft-sub" style={{ margin: 0 }}>
        {message}
      </p>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Thumb — resolves a media reference to a URL
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Media lives in IndexedDB as a Blob, so a thumbnail is an async lookup, not a
 * src string. Falls back to the uploaded server path when the local Blob has
 * been evicted under quota pressure, then to the company's initials.
 */
export function Thumb({ photo, initials, size = 56, alt = '', rounded = true }) {
  const [url, setUrl] = useState(null);
  const idRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const id = photo && (photo.mediaId || photo.id);

    if (!id) {
      setUrl(photo && photo.path ? assetUrl(photo.path) : null);
      return undefined;
    }
    idRef.current = id;
    mediaUrl(id).then((u) => {
      if (cancelled) return;
      setUrl(u || (photo.path ? assetUrl(photo.path) : null));
    });
    return () => {
      cancelled = true;
    };
  }, [photo]);

  useEffect(
    () => () => {
      if (idRef.current) releaseUrl(idRef.current);
    },
    [],
  );

  const style = { width: size, height: size, borderRadius: rounded ? 10 : 0 };

  if (!url) {
    return (
      <span className="ft-thumb ft-thumb-fallback" style={style} aria-hidden={!alt}>
        {(initials || '?').slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return <img className="ft-thumb" style={style} src={url} alt={alt} />;
}

/** Server-served media path → URL. nginx serves the tree at the site root in
 *  production; AssetController serves it at /asset/** in dev. */
export function assetUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  const base = process.env.REACT_APP_API_ENDPOINT || '';
  return `${base}/asset/${String(path).replace(/^\/+/, '')}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Stat tile (spec § 5.3) — tappable, because every tile is also a filter
   ═════════════════════════════════════════════════════════════════════════ */

export function Stat({ label, value, to, onClick, tone }) {
  const inner = (
    <>
      <span className={`ft-stat-n${tone ? ` ft-stat-${tone}` : ''}`}>{value}</span>
      <span className="ft-stat-l">{label}</span>
    </>
  );
  if (to) {
    return (
      <Link className="ft-stat" to={to}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className="ft-stat" onClick={onClick}>
      {inner}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Empty state
   ═════════════════════════════════════════════════════════════════════════ */

export function Empty({ title, children, action }) {
  return (
    <div className="ft-empty">
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Supplier card — used on the dashboard, the suppliers list, plan matches
   and the shortlist (spec § 5.3)
   ═════════════════════════════════════════════════════════════════════════ */

export function SupplierCard({ supplier, product, fair, fairName, to }) {
  const p = product || (supplier.products || [])[0] || null;
  const photo = mainPhoto(supplier, p && p.id);
  const contact = primaryContact(supplier);
  const price = p ? priceLine(p, fair) : '';
  const count = (supplier.products || []).length;

  return (
    <Link className="ft-supcard" to={to || `/fair-tracker/s/${supplier.id}`}>
      <Thumb photo={photo} initials={supplier.company} size={56} alt="" />
      <div className="ft-supcard-main">
        <div className="ft-supcard-title">{(p && p.name) || supplier.company}</div>
        <div className="ft-supcard-meta">
          {[
            supplier.company,
            supplier.booth ? `Booth ${supplier.booth}` : null,
            fairName || null,
            count > 1 ? `${count} products` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {price && (
          <div className="ft-supcard-price">
            {price}
            {p && p.moq ? ` · MOQ ${p.moq}` : ''}
          </div>
        )}
        <div className="ft-supcard-pills">
          <Pill>{p ? p.decision : supplierDecision(supplier)}</Pill>
          {supplierRating(supplier) > 0 && <Stars value={supplierRating(supplier)} readOnly />}
          {p && p.nextStep && p.nextStep !== 'None' && <Pill tone="info">{p.nextStep}</Pill>}
          {p && p.revisit && <Pill tone="maybe">↻ {tr('Revisit')}</Pill>}
          {!p && contact && contact.name ? <span className="ft-faint">{contact.name}</span> : null}
        </div>
      </div>
      <Icon name="ChevronRight" size={18} />
    </Link>
  );
}

export { Field, Input, NumInput, Textarea, Select, Segment, Chips, Stars, Check, YesNo } from './controls';
